import { ReactElement } from "react";
import { useQuery, useQueryClient } from "react-query";
import AdministrationSiteTable from "../Components/Tables/AdministrationSiteTable";
import { ReactQueryKeys } from "../../../Constants/ClientRoutingConstants";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../Shared/Components/Cards/ErrorCard";
import administrationQueryService from "../Utilities/AdministrationAPIService";
import accountSettingsApiService from "../../AccountSettings/Utilities/AccountSettingsApiService";
import { useAccountSettingsStore } from "../../../Zustand/accountSettingsStore";
import { SiteReadDto } from "../../../Models/Site/SiteReadDto";
import { useToast } from "@chakra-ui/react";
import { SiteUpdateDto } from "../../../Models/Site/SiteUpdateDto";
import { SiteCreateDto } from "../../../Models/Site/SiteCreateDto";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import { useWindowSize } from "usehooks-ts";

export default function SuperAdministrationSitesPage(): ReactElement {
  const client = useQueryClient();
  const setSoftwareFeatures = useAccountSettingsStore((state) => state.setSoftwareFeatures);
  const setAccountTimeZone = useAccountSettingsStore((state) => state.setAccountTimeZone);
  const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });

  const localizationService = useLocalization();
  //get window size
  const { height } = useWindowSize()

  //Query for sites
  const sitesQuery = useQuery<Array<SiteReadDto>>(ReactQueryKeys.AllSitesQuery, async () => {
    return await administrationQueryService.getAllSites();
  });

  const refreshAccountSettingsInStore = async () => {
    try {
      const settings = await accountSettingsApiService.getAccountSettings();
      setSoftwareFeatures(settings.softwareFeatures);
      if (settings.timezone !== "") {
        setAccountTimeZone(settings.timezone);
      }
    } catch {
      // Keep existing store values if refresh fails
    }
  };

  const createSite = async (createSiteDto: SiteCreateDto) => {
    try {
      await administrationQueryService.createSite(createSiteDto);
      client.refetchQueries(ReactQueryKeys.AllSitesQuery);
      toast({
        title: `${localizationService.toLanguageString('custom.success', enMessages.custom.success)}`,
        description: `${localizationService.toLanguageString('custom.accountCreated', enMessages.custom.accountCreated)}`,
        status: "success",
      });
    } catch (e: any) {
      toast({
        title: `${localizationService.toLanguageString('custom.error', enMessages.custom.error)}`,
        description: `${localizationService.toLanguageString('custom.accountCreatedError', enMessages.custom.accountCreatedError)}`,
        status: "error",
      });
      throw e;
    }
  };

  const updateSite = async (siteId: string, updateSiteDto: SiteCreateDto) => {
    try {
      const siteUpdateDto: SiteUpdateDto = {
        ...updateSiteDto,
        id: siteId
      };
      await administrationQueryService.updateSite(siteUpdateDto);
      await client.refetchQueries(ReactQueryKeys.AllSitesQuery);
      await refreshAccountSettingsInStore();
      toast({
        title: `${localizationService.toLanguageString('custom.success', enMessages.custom.success)}`,
        description: `${localizationService.toLanguageString('custom.accountCreated', enMessages.custom.accountCreated)}`,
        status: "success",
      });
    } catch (e: any) {
      toast({
        title: `${localizationService.toLanguageString('custom.error', enMessages.custom.error)}`,
        description: `${localizationService.toLanguageString('custom.accountCreatedError', enMessages.custom.accountCreatedError)}`,
        status: "error",
      });
      throw e;
    }
  };

  const RenderQuery = () => {
    if (sitesQuery.error) {
      return <ErrorCard />;
    } else {
      return (
        <div  className="m-5" id="SitesPage">
          <div>
            <h1 className="gb-h1">{localizationService.toLanguageString('custom.manageAccounts', enMessages.custom.manageAccounts)}</h1>
            <h2 className="gb-text-normal mb-6">{localizationService.toLanguageString('custom.manageAccountsText', enMessages.custom.manageAccountsText)}</h2>
            <AdministrationSiteTable 
              updateSite={updateSite} 
              createSite={createSite}
              sites={sitesQuery.data || []} 
              height={height - 200}
            />
          </div>
        </div>
      );
    }
  };

  return sitesQuery.isLoading || sitesQuery.isFetching ? <LoadingSpinner /> : <RenderQuery />;
}
